import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

import { Field, Input, Label, Button } from '@headlessui/react';
import { useTranslation } from 'react-i18next';
import SpinnerIcon from '../shared/SpinnerIcon';
import GuestLayout from './GuestLayout';
import clsx from 'clsx';

const baseUrl = process.env.REACT_APP_API_URL;

const Signup: React.FC = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    // const defaultErrorMsg = 'An error occurred during verification';

    const [email, setEmail] = useState<string>('');
    const [name, setName] = useState<string>('');
    const [errorMsg, setErrorMsg] = useState<string | null>(null);
    const [isValid, setIsValid] = useState<boolean | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const token = localStorage.getItem('authToken');

    // check if user is already logged in
    useEffect(() => {
        const validateToken = async () => {
            try {
                const response = await axios.get(`${baseUrl}/auth/validate-token`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });

                const user = response.data;

                setIsValid(!!user); // if there is any valid user
            } catch (error) {
                localStorage.removeItem('authToken');
                setIsValid(false);
            }
        };

        token ? validateToken() : setIsValid(false);

        if (isValid) {
            navigate('/dashboard');
        }
    }, [navigate, token, isValid]);

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setErrorMsg(null);
        setIsLoading(true);
    };

    const inputClass = clsx(
        'block w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-base text-slate-900 shadow-sm',
        'placeholder:text-slate-400',
        'focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-blue-600',
        'transition duration-150 ease-in-out'
    );

    const buttonClass = clsx(
        'w-full inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 hover:bg-blue-700',
        'py-3 px-4 text-base font-semibold text-white shadow-sm',
        'focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2',
        'transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed'
    );

    return (
        <GuestLayout>
            <div className="mb-8">
                <h1 className="text-2xl font-semibold text-slate-900">{t('auth.signUp')}</h1>
                <p className="mt-1 text-sm text-slate-500">{t('auth.signupSubtitle')}</p>
            </div>

            {errorMsg && (
                <div role="alert" className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                    {errorMsg}
                </div>
            )}

            <form onSubmit={handleLogin} noValidate className="space-y-5">
                <Field>
                    <Label className="block text-sm font-medium text-slate-700 mb-1.5">{t('auth.fullName')}</Label>
                    <Input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        autoComplete="name"
                        autoFocus
                        required
                        placeholder={t('auth.fullNamePlaceholder')}
                        className={inputClass}
                    />
                </Field>

                <Field>
                    <Label className="block text-sm font-medium text-slate-700 mb-1.5">{t('auth.email')}</Label>
                    <Input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        autoComplete="email"
                        required
                        placeholder={t('auth.emailPlaceholder')}
                        className={inputClass}
                    />
                </Field>

                <Button type="submit" disabled={isLoading} className={buttonClass}>
                    {isLoading && <SpinnerIcon />}
                    {isLoading ? t('auth.registering') : t('auth.register')}
                </Button>
            </form>
        </GuestLayout>
    );
};

export default Signup;
